import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone, ChevronDown } from "lucide-react";
import { api, errMsg } from "../../lib/api";

const ROWS = [
  ["Social", "socials"], ["Stile", "style"], ["Data evento", "event_date"], ["Cerimonia", "ceremony_location"],
  ["Ricevimento", "reception_location"], ["Diurno/Serale", "timing"], ["Ospiti", "guests"], ["Budget", "budget"],
  ["Colori", "colors"], ["Ci ha conosciuto tramite", "referral"], ["Note", "notes"],
];
const STATUS = { nuova: "bg-terra text-cream", "in corso": "bg-brass text-ink", chiusa: "bg-stone text-ink" };

const Card = ({ q, onChange }) => {
  const [open, setOpen] = useState(false);
  const setStatus = async (status) => {
    try { await api.patch(`/inquiries/${q.id}`, { status }); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try { await api.delete(`/inquiries/${q.id}`); toast.success("Eliminata"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const wa = `https://wa.me/${(q.phone || "").replace(/\D/g, "").replace(/^(?!39)(3\d{8,9})$/, "39$1")}`;
  return (
    <div data-testid={`inquiry-${q.id}`} className="bg-white border border-stone">
      <button type="button" data-testid={`inquiry-toggle-${q.id}`} onClick={() => setOpen((o) => !o)} className="w-full p-5 flex items-center gap-4 text-left">
        <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS[q.status] || STATUS.nuova}`}>{q.status}</span>
        <span className="flex-1 min-w-0">
          <span className="block font-serif text-xl text-ink truncate">{q.couple_names}</span>
          <span className="block text-xs text-ash">{q.event_date || "data da definire"} · ricevuta il {new Date(q.created_at).toLocaleDateString("it-IT")}</span>
        </span>
        <ChevronDown className={`w-5 h-5 text-ash transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-stone">
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1.5 rounded-full border border-stone px-3 py-1.5 text-sm"><Mail className="w-3.5 h-3.5" /> {q.email}</a>
            <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-stone px-3 py-1.5 text-sm"><Phone className="w-3.5 h-3.5" /> {q.phone}</a>
          </div>
          <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {ROWS.map(([l, k]) => (
              <div key={k} className={k === "notes" ? "sm:col-span-2" : ""}>
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash">{l}</dt>
                <dd className="text-sm text-ink whitespace-pre-line">{q[k] || "—"}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <select data-testid={`inquiry-status-${q.id}`} value={q.status} onChange={(e) => setStatus(e.target.value)} className="field !w-auto !py-2">
              <option value="nuova">Nuova</option><option value="in corso">In corso</option><option value="chiusa">Chiusa</option>
            </select>
            <button data-testid={`inquiry-delete-${q.id}`} onClick={remove} className="inline-flex items-center gap-1.5 rounded-full border border-stone px-3 py-2 text-sm text-red-700"><Trash2 className="w-3.5 h-3.5" /> Elimina</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const Inquiries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const r = await api.get("/inquiries"); setItems(Array.isArray(r.data) ? r.data : []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <p className="text-ash">Caricamento…</p>;
  if (!items.length) return <p data-testid="inquiries-empty" className="text-ash">Nessuna richiesta per ora.</p>;
  return <div data-testid="inquiries-list" className="space-y-3">{items.map((q) => <Card key={q.id} q={q} onChange={load} />)}</div>;
};
